import React, { useEffect, useRef } from 'react'
import styles from './modal.module.scss'

export type ModalProps = {
  /** Controlled: whether the modal is visible */
  open: boolean
  /** Dialog title — used as aria-label */
  title: string
  /** Called when the user requests to close (Esc, backdrop click, close button) */
  onClose: () => void
  children: React.ReactNode
  /** Maximum panel width override (CSS value, e.g. '80rem') */
  maxWidth?: string
}

/**
 * Accessible modal dialog with focus management, Esc to close, backdrop click.
 * State is controlled by the caller.
 * Presentational only — no tRPC / domain types.
 */
export const Modal = ({
  open,
  title,
  onClose,
  children,
  maxWidth,
}: ModalProps): React.ReactElement | null => {
  const panelRef = useRef<HTMLDivElement>(null)

  // Focus the panel on open so Esc works and keyboard navigates inside
  useEffect(() => {
    if (open) {
      panelRef.current?.focus()
    }
  }, [open])

  // Esc to close
  useEffect(() => {
    if (!open) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handler)
    return () => { document.removeEventListener('keydown', handler) }
  }, [open, onClose])

  if (!open) return null

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  const panelStyle = maxWidth != null ? { maxWidth } : undefined

  return (
    <div
      className={`row-flx flx-a-c flx-j-c p-24 ${styles['modal__backdrop']}`}
      data-testid="modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`col-flx border-m bg-white full-width ${styles['modal__panel']}`}
        style={panelStyle}
        tabIndex={-1}
        // Prevent backdrop click from triggering when clicking inside panel
        onClick={(event) => event.stopPropagation()}
      >
        <div className={`row-flx flx-a-c flx-j-b itm-flx-s-0 p-h-24 p-b-16 ${styles['modal__header']}`}>
          <h2 className={`m-0 txt-kale ${styles['modal__title']}`}>{title}</h2>
          <button
            type="button"
            className={`row-flx flx-a-c flx-j-c itm-flx-s-0 border-xs txt-light-kale ${styles['modal__close']}`}
            onClick={onClose}
            aria-label="Cerrar"
          >
            {/* U+00D7 MULTIPLICATION SIGN — looks like an ASCII "x" but is not
                one; do not "fix" it, the letter renders lopsided at this size. */}
            ×
          </button>
        </div>
        <div className={styles['modal__body']}>{children}</div>
      </div>
    </div>
  )
}
